import type { LocalComment } from '../storage';

const PIN_W = 320;
const PIN_MIN_H = 180;
const PIN_MARGIN = 12;

export type PinPosition = {
  left: number;
  top: number;
  arrow: 'tl' | 'tr' | 'bl' | 'br';
};

export function pinPosition(anchor: { vx: number; vy: number }): PinPosition {
  const cx = anchor.vx;
  const cy = anchor.vy;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const fitsRight = cx + PIN_W + PIN_MARGIN <= vw;
  const fitsBottom = cy + PIN_MIN_H + PIN_MARGIN <= vh;

  let left = fitsRight ? cx + PIN_MARGIN : cx - PIN_W - PIN_MARGIN;
  let top = fitsBottom ? cy + PIN_MARGIN : cy - PIN_MIN_H - PIN_MARGIN;
  left = Math.max(PIN_MARGIN, Math.min(vw - PIN_W - PIN_MARGIN, left));
  top = Math.max(PIN_MARGIN, Math.min(vh - PIN_MIN_H - PIN_MARGIN, top));

  const arrow = ((fitsBottom ? 't' : 'b') + (fitsRight ? 'l' : 'r')) as 'tl' | 'tr' | 'bl' | 'br';
  return { left, top, arrow };
}

// Hybrid anchor: prefers the resolved DOM element's bounding rect + stored
// fractional offset so pins scale with viewport changes. Falls back to stored
// absolute document coordinates when the selector no longer resolves (legacy
// comments without an offset, deleted element, mobile-only collapsed nav, …).
export function viewportAnchor(
  c: LocalComment,
  scroll: { x: number; y: number },
): { vx: number; vy: number } {
  const pct = c.clickOffsetPct;
  if (pct && c.elementSelector) {
    const el = resolveCommentElement(c);
    if (el) {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) {
        return {
          vx: r.left + r.width * pct.x,
          vy: r.top + r.height * pct.y,
        };
      }
    }
  }
  return { vx: c.docX - scroll.x, vy: c.docY - scroll.y };
}

// Resolves the original DOM element for a saved comment, picking the closest
// match by click point if the stored selector is ambiguous. This keeps legacy
// comments (saved with the older non-unique selector) highlighting the right
// card.
export function resolveCommentElement(c: LocalComment): HTMLElement | null {
  if (!c.elementSelector) return null;
  let matches: NodeListOf<Element>;
  try {
    matches = document.querySelectorAll(c.elementSelector);
  } catch {
    return null;
  }
  if (matches.length === 0) return null;
  if (matches.length === 1) return matches[0] as HTMLElement;

  let best: HTMLElement | null = null;
  let bestDist = Infinity;
  for (const m of Array.from(matches)) {
    if (!(m instanceof HTMLElement)) continue;
    const r = m.getBoundingClientRect();
    const cx = r.left + window.scrollX + r.width / 2;
    const cy = r.top + window.scrollY + r.height / 2;
    const dx = cx - c.docX;
    const dy = cy - c.docY;
    const dist = dx * dx + dy * dy;
    if (dist < bestDist) {
      bestDist = dist;
      best = m;
    }
  }
  return best;
}
